import { useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button, Stack, Text, TextInput } from '@mantine/core';
import { IconCamera } from '@tabler/icons-react';
import {
  useRidesMeService,
  RIDES_ERROR_CODES,
  RIDES_SEX,
  RIDES_VERIFICATION_STATUS,
  RIDES_VERIFICATION_TYPE,
  type RidesUpsertDriverProfileRequest,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useDriverProfile, useMemberMe, useMyVerifications, useInvalidateMe, findVerification, isSubmitted } from '@/lib/me';
import { useRidesLookups } from '@/lib/lookups';
import { parseApiDate, toYmd } from '@/lib/format';
import { useRidesWizard } from '@/store/ridesWizard';
import { useCopy, seatRange, photoRules, driverStandards, documentRules } from '@/lib/settings';
import { hasErrorCode } from '@/lib/copy';
import { docsUsable, typesFor, useMyDocuments } from '@/lib/documents';
import {
  EMPTY_EMERGENCY,
  declarationAcceptedFor,
  driverUpsertFromView,
  emergencyFromView,
  emergencyRequest,
  emergencyValid,
  experienceOk,
  type EmergencyDraft,
} from '@/lib/driverStandards';
import { useMyPhoto } from '@/lib/photo';
import { StepHeader } from '@/components/ui/StepHeader';
import { StickyActionBar } from '@/components/ui/StickyActionBar';
import { SectionCard } from '@/components/ui/SectionCard';
import { Callout } from '@/components/ui/Callout';
import { PickerCard, PickerGroup } from '@/components/ui/PickerCard';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { LicenceForm, EMPTY_LICENCE, licenceDraftValid, useLicenceRuleOptions, type LicenceDraft } from '@/components/rides/LicenceForm';
import { ConsentCheckbox } from '@/components/rides/ConsentCheckbox';
import { ProfilePhotoCard } from '@/components/rides/ProfilePhotoCard';
import { DriverDeclarationCard } from '@/components/rides/DriverDeclarationCard';
import { EmergencyContactCard } from '@/components/rides/EmergencyContactCard';
import { LicenceExperienceField } from '@/components/rides/LicenceExperienceField';
import { ErrorAlert } from '@/components/rides/ErrorAlert';
import { ReadinessList, useDriverReadiness } from '@/components/flows/DriverReadiness';
import { EditHeader, setupPlan, type SetupPart } from './_shared';
import '@/styles/flows.css';

type Part = 'you' | 'car' | 'licence' | 'contact' | 'promise';
const PARTS: Part[] = ['you', 'car', 'licence', 'contact', 'promise'];

/**
 * Drive, "Get ready to drive". Without ?part= it is the readiness checklist: what a driver needs
 * before riders can book (photo, car, licence, ID check, emergency contact, driver promise,
 * documents), each with its status, so blockers show up front, not on the last step.
 *
 * ?part=you|car|licence|contact|promise is one short screen per topic. A new driver goes through
 * them in order and everything is saved on the last one (PUT me/driver + the licence check); an
 * existing driver edits one topic and saves it on its own (rebuilt from the saved profile).
 */
export default function DriveProfilePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { getUserDisplayName } = useEnrichedAuth();
  const me = useRidesMeService();
  const driver = useDriverProfile();
  const member = useMemberMe();
  const verifications = useMyVerifications();
  const lookups = useRidesLookups();
  const docs = useMyDocuments();
  const invalidate = useInvalidateMe();
  const setRole = useRidesWizard((s) => s.setRole);
  const setDriverDraft = useRidesWizard((s) => s.setDriver);
  const readiness = useDriverReadiness();
  const { t, settings } = useCopy();

  const [sexId, setSexId] = useState<number | null>(null);
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [colour, setColour] = useState('');
  const [plate, setPlate] = useState('');
  const [seats, setSeats] = useState(3);
  const [licence, setLicence] = useState<LicenceDraft>(EMPTY_LICENCE);
  const [consent, setConsent] = useState(false);
  const [firstYear, setFirstYear] = useState<number | null>(null);
  const [emergency, setEmergency] = useState<EmergencyDraft>(EMPTY_EMERGENCY);
  const [declChecked, setDeclChecked] = useState(false);
  const [tried, setTried] = useState(false);

  const q = params.get('part') as Part | null;
  const part: Part | null = q && PARTS.includes(q) ? q : null;
  const nextParam = params.get('next');
  const returnTo = nextParam && nextParam.startsWith('/drive/') ? nextParam : '/drive/profile';

  const d = driver.data;
  const isNew = !driver.isLoading && !driver.error && d === null;
  const licV = findVerification(verifications.data, RIDES_VERIFICATION_TYPE.DriversLicence);
  const idV = findVerification(verifications.data, RIDES_VERIFICATION_TYPE.GovernmentId);
  const licenceAlreadySubmitted = isSubmitted(licV);
  const licenceOpts = useLicenceRuleOptions();
  const seatChoices = seatRange(settings);
  const photos = photoRules(settings);
  const myPhoto = useMyPhoto();
  const photoMissing = photos.driverRequired && !myPhoto.hasPhoto;
  const standards = driverStandards(settings);
  const declAccepted = declarationAcceptedFor(d, standards.declarationTextVersion);
  const needDeclaration = standards.declarationRequired && !declAccepted;
  const askYear = standards.minimumYearsLicensed > 0;
  const emergencyLevel = standards.driverEmergency;
  const docRules = documentRules(settings);
  const docsAsked = docRules.enabled && docsUsable(docs.data) && typesFor(docs.data, 'driver', { clearanceForDrivers: docRules.minorRidesRequireClearedDriver }).length > 0;

  useEffect(() => { setRole('driver'); }, [setRole]);

  // Prefill from the saved driver profile (or the member's sex for a new driver).
  useEffect(() => {
    if (d) {
      setSexId(d.sexID || null);
      setMake(d.vehicle.make);
      setModel(d.vehicle.model);
      setColour(d.vehicle.colour);
      setPlate(d.vehicle.plate);
      setSeats(d.vehicle.seats);
      setLicence({
        licenceTypeID: d.licence.licenceTypeID,
        licenceCodeID: d.licence.licenceCodeID ?? null,
        country: d.licence.country,
        expiresAt: parseApiDate(d.licence.expiresAt),
        inEnglish: d.licence.inEnglish,
        hasIdp: d.licence.hasIdp,
        licenceNumber: '',
      });
      setConsent(true);
      setFirstYear(d.licenceFirstIssuedYear ?? null);
      setEmergency(emergencyFromView(d.emergencyContact));
    } else if (member.data?.gender) {
      const g = member.data.gender.toLowerCase();
      setSexId(g.startsWith('f') ? RIDES_SEX.Female : g.startsWith('m') ? RIDES_SEX.Male : null);
    }
  }, [d, member.data]);

  useEffect(() => { setTried(false); }, [part]);

  // ---------------------------------------------------------------- what each screen needs
  const vehicleOk = make.trim().length > 1 && colour.trim().length > 0 && plate.trim().length >= 4 && seats >= seatChoices[0];
  const numberTyped = licence.licenceNumber.trim().length > 0;
  const licenceDetailsOk = licenceAlreadySubmitted && !numberTyped ? true : licenceDraftValid(licence, licenceOpts);
  const yearOk = !askYear || experienceOk(firstYear, standards.minimumYearsLicensed);
  const emergencyOk = emergencyValid(emergency, emergencyLevel);
  const ok: Record<Part, boolean> = {
    you: !!sexId && !photoMissing,
    car: vehicleOk,
    licence: licenceDetailsOk && yearOk,
    contact: emergencyOk,
    promise: (!needDeclaration || declChecked) && (!!d || consent),
  };

  const newParts: Part[] = ['you', 'car', 'licence', ...(emergencyLevel !== 'notAsked' ? (['contact'] as Part[]) : []), 'promise'];
  const plan: SetupPart[] = setupPlan({ contactAsked: emergencyLevel !== 'notAsked', idNeeded: !isSubmitted(idV), licenceFix: false, docsAsked });

  const licenceBody = () => ({
    licenceTypeID: licence.licenceTypeID,
    licenceCodeID: licence.licenceCodeID,
    country: licence.country || 'ZAF',
    expiresAt: licence.expiresAt ? toYmd(licence.expiresAt) : null,
    inEnglish: licence.inEnglish,
    hasIdp: licence.hasIdp,
  });
  const startLicenceCheck = () =>
    me.startVerification(RIDES_VERIFICATION_TYPE.DriversLicence, { ...licenceBody(), licenceNumber: licence.licenceNumber.trim() });

  /** New driver: everything at once on the last screen. */
  const saveNew = useMutation({
    mutationFn: async () => {
      const body: RidesUpsertDriverProfileRequest = {
        sexID: sexId!,
        displayName: getUserDisplayName(),
        mobile: member.data?.cellNumber ?? null,
        vehicle: { make: make.trim(), model: model.trim(), colour: colour.trim(), plate: plate.trim().toUpperCase(), seats },
        licence: licenceBody(),
        dataConsent: { granted: true, textVersion: lookups.data?.settings?.dataConsentTextVersion ?? '1' },
        licenceFirstIssuedYear: askYear ? firstYear : undefined,
        emergencyContact: emergencyLevel !== 'notAsked' ? emergencyRequest(emergency) : undefined,
        declaration: needDeclaration && declChecked ? { textVersion: standards.declarationTextVersion, granted: true } : undefined,
      };
      await me.upsertDriver(body);
      if (!licenceAlreadySubmitted || numberTyped) await startLicenceCheck();
      setLicence((l) => ({ ...l, licenceNumber: '' }));
    },
    onSuccess: () => {
      invalidate();
      setDriverDraft({ seats });
      navigate('/drive/verify?setup=1');
    },
  });

  /** Existing driver: save just this topic, rebuilt from the saved profile. */
  const savePart = useMutation({
    mutationFn: async (p: Part) => {
      if (!d) return;
      const patch: Partial<RidesUpsertDriverProfileRequest> = {};
      if (p === 'you') patch.sexID = sexId!;
      if (p === 'car') patch.vehicle = { make: make.trim(), model: model.trim(), colour: colour.trim(), plate: plate.trim().toUpperCase(), seats };
      if (p === 'licence') {
        patch.licence = licenceBody();
        if (askYear) patch.licenceFirstIssuedYear = firstYear;
      }
      if (p === 'contact') patch.emergencyContact = emergencyRequest(emergency);
      if (p === 'promise' && needDeclaration) patch.declaration = { textVersion: standards.declarationTextVersion, granted: true };
      await me.upsertDriver(driverUpsertFromView(d, patch));
      if (p === 'licence' && (!licenceAlreadySubmitted || numberTyped)) await startLicenceCheck();
      setLicence((l) => ({ ...l, licenceNumber: '' }));
    },
    onSuccess: () => {
      invalidate();
      navigate(returnTo);
    },
  });

  const saving = saveNew.isPending || savePart.isPending;
  const saveError = saveNew.error ?? savePart.error;
  const yearRefused = hasErrorCode(saveError, RIDES_ERROR_CODES.LicenceExperienceRequired);
  const emergencyRefused = hasErrorCode(saveError, RIDES_ERROR_CODES.EmergencyContactRequired);
  const declarationRefused = hasErrorCode(saveError, RIDES_ERROR_CODES.DeclarationRequired);

  const loading = driver.isLoading || member.isLoading || verifications.isLoading;

  // ---------------------------------------------------------------- the checklist (no ?part=)
  if (!part) {
    const overall = readiness.overall;
    const firstTodo = readiness.todos.find((i) => i.to);
    const title = overall === 'ready' ? "You're ready to drive" : 'Get ready to drive';
    let primary: ReactNode = null;
    if (!readiness.loading && !readiness.error) {
      if (isNew) primary = <Button fullWidth onClick={() => navigate('/drive/profile?part=you')}>Start</Button>;
      else if (overall === 'todo' && firstTodo?.to) primary = <Button fullWidth onClick={() => navigate(firstTodo.to!)}>Continue</Button>;
      else if (overall === 'ready') primary = <Button fullWidth component={Link} to="/drive/events">Offer a lift</Button>;
    }
    const todoCount = readiness.todos.length;
    return (
      <div className="rides-main rides-flow">
        <header className="rides-stephead rides-edithead">
          <h1 className="rides-title">{title}</h1>
          <p className="rides-lede">{overall === 'ready' ? t('drive.ready_done') : t('drive.ready_intro')}</p>
        </header>
        {readiness.loading ? (
          <LoadingState label="Loading your checklist" variant="rows" count={6} />
        ) : readiness.error ? (
          <ErrorState title="We couldn't load your driver details" error={readiness.error} onRetry={() => { driver.refetch(); verifications.refetch(); }} />
        ) : (
          <div className="rides-flow-stack">
            {overall === 'todo' && <Callout tone="warn">{t('drive.ready_todo')}</Callout>}
            {overall === 'waiting' && <Callout tone="info" title="Your checks are not finished yet">{t('drive.ready_waiting')}</Callout>}
            <SectionCard title={isNew ? "What we'll ask for" : 'Your checklist'} description={isNew ? t('drive.ready_new') : undefined}>
              <ReadinessList items={readiness.items} interactive={!isNew} />
            </SectionCard>
            {d && d.openListings > 0 && (
              <Button variant="default" fullWidth component={Link} to="/drive/listings">See my lifts</Button>
            )}
            {overall === 'waiting' && (
              <Button variant="default" fullWidth component={Link} to="/home">Back to Home</Button>
            )}
          </div>
        )}
        {primary && <StickyActionBar hint={overall === 'todo' && todoCount ? (todoCount === 1 ? '1 thing to do' : `${todoCount} things to do`) : undefined}>{primary}</StickyActionBar>}
      </div>
    );
  }

  // ---------------------------------------------------------------- one topic
  if (!loading && !isNew && !d) return <Navigate to="/drive/profile" replace />;
  if (part === 'contact' && emergencyLevel === 'notAsked') return <Navigate to={isNew ? '/drive/profile?part=promise' : '/drive/profile'} replace />;

  const titles: Record<Part, string> = {
    you: 'About you',
    car: 'Your car',
    licence: "Your driver's licence",
    contact: 'Who should we call in an emergency?',
    promise: standards.declarationRequired ? 'Your driver promise' : 'Your consent',
  };
  const ledes: Record<Part, string> = {
    you: t('drive.you_intro'),
    car: t('drive.car_intro'),
    licence: t('drive.licence_intro'),
    contact: t('emergency.intro'),
    promise: t('drive.promise_intro'),
  };

  const newIndex = newParts.indexOf(part);
  const isLastNew = isNew && newIndex === newParts.length - 1;
  const onPrimary = () => {
    setTried(true);
    if (!ok[part]) return;
    if (!isNew) { savePart.mutate(part); return; }
    if (!isLastNew) { navigate(`/drive/profile?part=${newParts[newIndex + 1]}`); return; }
    const firstBad = newParts.find((p) => !ok[p]);
    if (firstBad) { navigate(`/drive/profile?part=${firstBad}`); return; }
    saveNew.mutate();
  };

  let body: ReactNode = null;
  if (part === 'you') {
    body = (
      <div className="rides-flow-stack">
        <SectionCard>
          <Text c="dimmed" size="sm">Your name</Text>
          <Text fw={700} size="lg">{getUserDisplayName() || 'Not set'}</Text>
          <Text c="dimmed" size="sm" mt={4}>This comes from your Acts Church account. If it doesn't match your ID, please contact the church office.</Text>
        </SectionCard>
        <PickerGroup label="Your sex, as on your ID">
          <PickerCard selected={sexId === RIDES_SEX.Female} onSelect={() => setSexId(RIDES_SEX.Female)} title="Female" />
          <PickerCard selected={sexId === RIDES_SEX.Male} onSelect={() => setSexId(RIDES_SEX.Male)} title="Male" />
        </PickerGroup>
        {tried && !sexId && <Text c="var(--rides-bad)" fw={600} role="alert">Please choose one.</Text>}
        {settings.femaleDriverRuleEnabled && <p className="rides-help">{t('matches.female_driver_note')}</p>}
        {photos.enabled && (
          <div>
            <h2 className="rides-sublabel">Profile photo{photos.driverRequired ? '' : ' (optional)'}</h2>
            <ProfilePhotoCard
              notice={
                photoMissing ? (
                  <Callout tone={tried ? 'bad' : 'warn'} icon={<IconCamera size={20} />}>{t('photo.driver_required')}</Callout>
                ) : !myPhoto.hasPhoto && !myPhoto.isLoading ? (
                  <Callout tone="info" icon={<IconCamera size={20} />}>{t('photo.driver_nudge')}</Callout>
                ) : undefined
              }
              removeWarning={photos.driverRequired ? "You'll need a photo again before you can offer a lift." : undefined}
            />
          </div>
        )}
      </div>
    );
  }
  if (part === 'car') {
    body = (
      <div className="rides-flow-stack">
        <div className="rides-fieldgrid two">
          <TextInput label="Make" placeholder="For example, Toyota" value={make} onChange={(e) => setMake(e.currentTarget.value)} required error={tried && make.trim().length < 2 ? 'Please enter the make.' : undefined} />
          <TextInput label="Model" description="Optional" placeholder="For example, Corolla" value={model} onChange={(e) => setModel(e.currentTarget.value)} />
        </div>
        <div className="rides-fieldgrid two">
          <TextInput label="Colour" placeholder="For example, white" value={colour} onChange={(e) => setColour(e.currentTarget.value)} required error={tried && !colour.trim() ? 'Please enter the colour.' : undefined} />
          <TextInput
            label="Number plate"
            placeholder="For example, CA 123-456"
            className="rides-plateinput"
            value={plate}
            onChange={(e) => setPlate(e.currentTarget.value.toUpperCase())}
            required
            autoCapitalize="characters"
            error={tried && plate.trim().length < 4 ? 'Please enter the number plate.' : undefined}
          />
        </div>
        <div>
          <h2 className="rides-sublabel" id="seats-label">How many passengers can you take?</h2>
          <div className="rides-seatrow" style={{ ['--seat-count' as string]: Math.min(seatChoices.length, 8) }} role="radiogroup" aria-labelledby="seats-label">
            {seatChoices.map((n) => (
              <button key={n} type="button" role="radio" className="rides-chip" aria-checked={seats === n} onClick={() => setSeats(n)}>{n}</button>
            ))}
          </div>
          <p className="rides-help" style={{ marginTop: 8 }}>Count the seats with a seatbelt, not the driver's seat.</p>
        </div>
      </div>
    );
  }
  if (part === 'licence') {
    body = (
      <div className="rides-flow-stack">
        {licenceAlreadySubmitted && (
          <Callout tone="info">Your licence has been {licV?.statusID === RIDES_VERIFICATION_STATUS.Verified ? 'checked' : 'sent for a check'}. Only fill in the number again if your licence has changed.</Callout>
        )}
        <LicenceForm value={licence} onChange={setLicence} disabled={saving} />
        {tried && !licenceDetailsOk && <Text c="var(--rides-bad)" fw={600} role="alert">Please fill in the licence code, number and expiry date.</Text>}
        {askYear && (
          <LicenceExperienceField value={firstYear} onChange={setFirstYear} minYears={standards.minimumYearsLicensed} apiError={yearRefused} disabled={saving} showEmptyError={tried} />
        )}
      </div>
    );
  }
  if (part === 'contact') {
    body = <EmergencyContactCard id="emergency" value={emergency} onChange={setEmergency} level={emergencyLevel} highlight={emergencyRefused || (tried && !emergencyOk)} disabled={saving} />;
  }
  if (part === 'promise') {
    body = (
      <Stack gap="lg">
        {standards.declarationRequired && (
          <DriverDeclarationCard id="declaration" checked={declChecked} onChange={setDeclChecked} accepted={declAccepted} highlight={declarationRefused || (tried && needDeclaration && !declChecked)} disabled={saving} />
        )}
        {isNew && (
          <div>
            <ConsentCheckbox checked={consent} onChange={setConsent} />
            {tried && !consent && <Text c="var(--rides-bad)" fw={600} mt={6} role="alert">Please tick the box to agree.</Text>}
          </div>
        )}
      </Stack>
    );
  }

  const primaryLabel = !isNew ? 'Save' : isLastNew ? 'Save and continue' : 'Continue';
  const backTo = isNew ? (newIndex > 0 ? `/drive/profile?part=${newParts[newIndex - 1]}` : '/drive/profile') : returnTo;

  return (
    <div className="rides-main rides-flow">
      {isNew ? (
        <StepHeader step={plan.indexOf(part) + 1} total={plan.length} title={titles[part]} lede={ledes[part]} backTo={backTo} />
      ) : (
        <EditHeader title={titles[part]} lede={ledes[part]} backTo={backTo} backLabel={returnTo === '/drive/profile' ? 'Back to your checklist' : 'Back'} />
      )}
      {loading ? (
        <LoadingState label="Loading your driver details" count={2} />
      ) : (
        <>
          <div className="rides-stepbody" key={part ?? 'none'}>{body}</div>
          {!(yearRefused || emergencyRefused || declarationRefused) && saveError && (
            <div style={{ marginTop: 16 }}><ErrorAlert error={saveError} title="We couldn't save your details" /></div>
          )}
          <StickyActionBar hint={tried && !ok[part] ? 'Please fill in the items marked above.' : undefined}>
            <Button fullWidth loading={saving} onClick={onPrimary}>{primaryLabel}</Button>
          </StickyActionBar>
        </>
      )}
    </div>
  );
}
